import clsx from "clsx";
import { Link, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { showToast } from "@/components/ui/Toast";
import {
  DASHBOARD_CARDS,
  type DashboardCardData,
} from "@/features/dashboard/dashboardCards";
import { DashboardSkeleton } from "@/features/dashboard/DashboardSkeleton";
import { EmptyModules } from "@/features/dashboard/EmptyModules";

const NAVY = "#001f3f";

const filterVisible = (
  card: DashboardCardData,
  isAdmin: boolean,
  hasPermission: (perm: string) => boolean,
) => {
  if (card.adminOnly) return isAdmin;
  return (
    Boolean(card.public) ||
    !card.requires ||
    isAdmin ||
    hasPermission(card.requires)
  );
};

interface ModuleTileProps {
  card: DashboardCardData;
}

const ModuleTile = ({ card }: ModuleTileProps) => {
  const Icon: LucideIcon = card.icon;
  return (
    <Link
      to={card.to}
      aria-label={card.title}
      className={clsx(
        "group flex min-h-[116px] flex-col items-center justify-start rounded-lg px-2 pt-2 pb-2 text-center text-ink-primary",
        "transition-all duration-200 hover:-translate-y-1 hover:bg-accent-blue-light/40 hover:shadow-elevated",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue",
      )}
    >
      <div className="relative mb-3 flex h-[67px] w-[67px] flex-shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-accent-blue">
        <Icon className="h-7 w-7" strokeWidth={1.6} />
      </div>
      <span className="max-w-[220px] text-sm font-semibold leading-snug">
        {card.title}
      </span>
    </Link>
  );
};

export const DashboardPage = () => {
  const { user, loading, hasPermission, isAdmin } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();

  if (loading && !user) {
    return <DashboardSkeleton />;
  }

  const visibleCards = DASHBOARD_CARDS.filter((card) =>
    filterVisible(card, isAdmin, hasPermission),
  );

  const goHerramientas = () => {
    navigate("/herramientas");
  };

  return (
    <div className="min-h-full">
      <nav
        aria-label="Pestañas del dashboard"
        className="flex h-[55px] items-end border-b pl-2"
        style={{ borderColor: NAVY, backgroundColor: "#ffffff" }}
      >
        <div
          className="flex h-[50px] items-center rounded-t-md border bg-white px-3 text-sm font-semibold"
          style={{
            borderColor: NAVY,
            borderBottomColor: "#ffffff",
            color: NAVY,
          }}
        >
          G&D CONSULTING GROUP
        </div>
        <button
          type="button"
          onClick={goHerramientas}
          className={clsx(
            "flex h-[50px] items-center rounded-t-md border border-transparent bg-transparent px-3 text-sm font-semibold",
            "hover:border-accent-blue hover:text-accent-blue",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue",
          )}
          style={{ color: NAVY }}
        >
          Herramientas
        </button>
      </nav>

      <main className="mt-8 overflow-hidden rounded-t-lg border border-slate-200 bg-bg-secondary">
        <header className="flex min-h-[56px] items-center border-b border-slate-200 px-7">
          <h1 className="text-[21px] font-bold text-ink-primary">
            Te damos la bienvenida, ¿Qué deseas hacer?
          </h1>
        </header>

        {visibleCards.length === 0 ? (
          <EmptyModules />
        ) : (
          <section
            aria-label="Módulos"
            className="grid grid-cols-1 gap-x-6 gap-y-10 px-9 py-4 pb-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          >
            {visibleCards.map((card) => (
              <ModuleTile key={card.to} card={card} />
            ))}
          </section>
        )}
      </main>

      {import.meta.env.DEV && user && isEnabled("debug_show_session_info") ? (
        <details className="mt-8 rounded-lg border border-slate-200 bg-white p-3 text-xs text-ink-secondary">
          <summary className="cursor-pointer font-medium text-ink-primary">
            Sesión (debug)
          </summary>
          <pre className="mt-2 overflow-x-auto text-[11px]">
            {JSON.stringify(
              {
                id: user.id,
                email: user.email,
                rol_id: user.rol_id,
                permisos: user.permisos,
                empresa_id: user.empresa_id,
              },
              null,
              2,
            )}
          </pre>
        </details>
      ) : null}

      <button
        type="button"
        onClick={() => showToast("info", "Sistema de facturación", "Versión React 2024")}
        className="hidden"
        aria-hidden="true"
      />
    </div>
  );
};
